import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormattedMessage } from '../../../i18n/FormattedMessage';
import { PlusIcon } from '../../../components_2/AriesIcons';
import ChatScreenHeader from './ChatScreenHeader';
import ProfilePhoto from './ProfilePhoto';

const PeopleListHeader = ({
  showChatModal,
  isGroupChatEnabled,
  searchTerm,
  onSearchChange,
}) => {
  const intl = useIntl();
  return (
    <Fragment>
      <div className="chat-member-search">
        <span className="chat-member-search-icon material-icons-outlined">
          search
        </span>
        <input
          type="text"
          className="chat-member-search-input"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'chat.searchMember',
            defaultMessage: 'Search name of member',
          })}
        />
      </div>
      <button type="button" className="start-new-chat" onClick={showChatModal}>
        <span className="start-new-chat-icon">
          <PlusIcon size={16} />
        </span>
        <span>
          <FormattedMessage id={'Start New '} defaultMessage={'Start New '} />
          {isGroupChatEnabled ? (
            <FormattedMessage id={'Group '} defaultMessage={'Group '} />
          ) : null}
          <FormattedMessage id={'Chat'} defaultMessage={'Chat'} />
        </span>
      </button>
    </Fragment>
  );
};

PeopleListHeader.propTypes = {
  isGroupChatEnabled: PropTypes.bool.isRequired,
  showChatModal: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
};

const PeopleListItem = ({
  senderName,
  senderImg,
  lastMsgContent,
  lastMsgTime,
  unreadCount,
  chatId,
  activeChatId,
  onClick,
}) => {
  const isActive = activeChatId === chatId;
  const isHighlighted = !isActive && !!unreadCount;
  let className = 'clearfix';
  if (isActive) {
    className = `${className} active`;
  }
  if (isHighlighted) {
    className = `${className} notification`;
  }
  return (
    <li className={className} onClick={onClick}>
      <ProfilePhoto label={senderName} size="44px" src={senderImg} />
      {isHighlighted && <div className="notify">{unreadCount}</div>}
      <div className="about">
        <div className="name">{senderName}</div>
        <div className="status">
          <p>{lastMsgContent}</p>
        </div>
      </div>
      <p className="time">{lastMsgTime}</p>
    </li>
  );
};

PeopleListItem.propTypes = {
  activeChatId: PropTypes.string.isRequired,
  chatId: PropTypes.string.isRequired,
  lastMsgContent: PropTypes.string.isRequired,
  lastMsgTime: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  senderName: PropTypes.string.isRequired,
  senderImg: PropTypes.string,
  unreadCount: PropTypes.number.isRequired,
};

const matchesSearch = (item, isGroupChatEnabled, term) => {
  const inTab =
    (isGroupChatEnabled && item.isGroup) ||
    (!isGroupChatEnabled && !item.isGroup);
  if (!inTab) return false;
  if (!term) return true;
  return (item.senderName || '').toLowerCase().includes(term.toLowerCase());
};

const PeopleList = ({
  peopleList,
  activeChatId,
  setActiveChatId,
  showChatModal,
  isGroupChatEnabled,
  onGroupClick,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const visibleChats = peopleList.filter((item) =>
    matchesSearch(item, isGroupChatEnabled, searchTerm)
  );
  return (
    <div
      className={`people-list ${activeChatId ? 'people-list-responsive' : ''}`}
      id="people-list"
    >
      <ChatScreenHeader
        onGroupClick={onGroupClick}
        isGroupActive={isGroupChatEnabled}
      />
      <PeopleListHeader
        showChatModal={showChatModal}
        isGroupChatEnabled={isGroupChatEnabled}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
      <ul className="list">
        {visibleChats.map((item) => (
          <PeopleListItem
            onClick={() => setActiveChatId({ chatId: item.chatId })}
            activeChatId={activeChatId}
            key={item.chatId}
            {...item}
          />
        ))}
        {visibleChats.length === 0 && (
          <li className="people-list-empty">
            <FormattedMessage
              id="chat.noResults"
              defaultMessage="No conversations found."
            />
          </li>
        )}
      </ul>
    </div>
  );
};

PeopleList.propTypes = {
  activeChatId: PropTypes.string.isRequired,
  isGroupChatEnabled: PropTypes.bool.isRequired,
  peopleList: PropTypes.array.isRequired,
  setActiveChatId: PropTypes.func.isRequired,
  showChatModal: PropTypes.func.isRequired,
  onGroupClick: PropTypes.func,
};

export default PeopleList;
